<script setup lang="ts">
import { Config } from 'dompurify';
import { escape, mapValues, merge } from 'lodash';
import { marked, MarkedOptions, Tokenizer } from 'marked';
import type { PartialDeep } from 'type-fest';
import { computed, MaybeRefOrGetter, toRef } from 'vue';
import Sanitize from '../Sanitize/Sanitize.vue';

export interface MarkdownOptions {
  marked?: MarkedOptions;
  purify?: Config;
}

const props = defineProps<{
  value: MaybeRefOrGetter<string | undefined>;
  options?: MarkdownOptions;
}>();

type TokenHandler = keyof Omit<Tokenizer, 'options' | 'rules' | 'lexer'>;

const processedTokens: Record<TokenHandler, boolean> = {
  code: false,
  fences: false,
  heading: false,
  hr: false,
  blockquote: false,
  list: false,
  html: false,
  def: false,
  table: false,
  lheading: false,
  text: false,
  escape: false,
  tag: false,
  link: false,
  reflink: false,
  emStrong: false,
  codespan: false,
  br: false,
  del: false,
  autolink: false,
  space: true,
  paragraph: true,
  url: true,
  inlineText: true,
};

const tokenizer = new Tokenizer();

mapValues(processedTokens, (isProcessed: boolean, token: TokenHandler) => {
  if (!isProcessed) {
    tokenizer[token] = () => undefined;
  }
});

const defaults: PartialDeep<MarkdownOptions> = {
  marked: {
    tokenizer,
  },
  purify: { ALLOWED_TAGS: ['a'] },
};

const options: MarkdownOptions = merge(defaults, props.options);

const dirtyMarkdown = toRef(props.value);

const dirtyHTML = computed(() =>
  dirtyMarkdown.value
    ? marked(escape(dirtyMarkdown.value), { ...options.marked, async: false })
    : undefined,
);
</script>

<template>
  <Sanitize :value="dirtyHTML" :options="options.purify" />
</template>
